import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';

import { EditorContext } from '../../../index';
import StoryboardContext from 'components/Storyboard/context';

import { toggleEditMode, updateWidgetEditorState, updateWidgetProperty } from 'actions/editor';
import { useChildrenWidgetSelected, useContainerSelector, usePageWidgetSelector, useWidgetChildrenSelector, useWidgetSelected, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { Property } from '../../../components/properties';
import { Background } from 'components/Editor/components/properties/background';
import { Container } from '../../core/container';
import { BackgroundView } from '../../background';
import Tooltip from 'components/Tooltip';

import { FlexFlowContext, resolveFlexLayout, resolveFlexMinHeight, useFlexMinHeightCapture } from '../../flex/flexContext';
import { FlexConfigDialog, flexContainerStyles } from '../../flex/flexConfig';

import styles from './style.module.scss';
import { getMediaInfo } from 'actions/pageHealth';
import { PAGE_CONTAIN_STORYBOARDS, isFlexModeAllowed } from 'constants/storyboardType';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

function alignmentMultiplier(alignment) {
  if (alignment === 'right') return 1;
  if (alignment === 'left') return 0;
  return 0.5;
}

const SectionColumnWidgetView = function SectionColumnWidget({ style, widgetProps, widgetRef, children, columnGap }) {
  const ref = widgetRef;
  const prevBottomRef = useRef(null);
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const parent = _.get(widgetProps, 'parent');
  const layout = _.get(widgetProps, 'properties.desktop.layout');
  const mobilelayout = _.get(widgetProps, 'properties.mobile.layout');
  const background = _.get(widgetProps, 'properties.desktop.background');

  const selected = useWidgetSelected(editor, id);
  const parentSelected = useWidgetSelected(editor, parent);
  const siblingSelection = useChildrenWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);

  const container = _.get(widgetProps, `properties.${breakpoint}.container`);
  const selectedParent = useContainerSelector(editor, _.get(widgetProps, 'uid'));
  const stretched = _.get(selectedParent, 'properties.desktop.stretched', false);
  const isLocked = _.get(widgetProps, `properties.desktop.locked`)

  const flexMode = _.get(widgetProps, 'properties.desktop.flexBoxEnabled', false);
  const sectionHasContent = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const columnIds = new Set(widgets.filter(w => w.parent === parent).map(w => w.uid));
    if (columnIds.size === 0) return false;
    return widgets.some(w => columnIds.has(w.parent));
  });

  const hasContent = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    return widgets.some(w => w.parent === id);
  });

  const flexLayout = React.useMemo(
    () => resolveFlexLayout(widgetProps, breakpoint),
    [
      breakpoint,
      JSON.stringify(_.get(widgetProps, 'properties.desktop.flexProperty')),
      JSON.stringify(_.get(widgetProps, 'properties.mobile.flexProperty'))
    ]
  );

  const flexContextValue = React.useMemo(
    () => ({ flexMode, flexLayout }),
    [flexMode, flexLayout]
  );

  useFlexMinHeightCapture({
    editor, id, breakpoint, flexMode, dispatch, widgetProps,
    getElement: (root) => root.querySelector('[data-flex-inner="true"]') || root,
    resolveFloor: (w, bp) => resolveFlexMinHeight(w, bp),
    resolveLayout: (w, bp) => resolveFlexLayout(w, bp),
    deps: [children, flexLayout],
  });

  useEffect(() => {
    if (selected && isLocked) {
      dispatch(updateWidgetEditorState(editor, widgetProps.parent, {
        manageColumns: false
      }));
    }
  }, [selected, isLocked])
  
  var widgetStyle = {
    ...style,
    transform: 'none',
    ...getStyles(widgetProps, breakpoint, columnGap, flexMode, sectionHasContent, hasContent)
  }

  const mobile = breakpoint === 'mobile';

  const minWidth = _.get(container, 'minWidth');
  const flexFloor = (mobile ? hasContent : sectionHasContent) ? 0 : 200;
  const flexInnerStyle = flexMode ? {
    ...flexContainerStyles(flexLayout, { minHeight: flexFloor }),
    height: '100%',
    ...(!mobile && stretched && minWidth
      ? {
        width: `${minWidth}px`,
        marginLeft: `calc((100% - ${minWidth}px) * ${alignmentMultiplier(_.get(container, 'alignment'))})`,
      }
      : { width: '100%' }),
  } : null;

  const getColumnBottomBoundary = (sectionColumn) => {  
    const innerTop = sectionColumn.getBoundingClientRect().top;
    const sectionChildren = sectionColumn.querySelectorAll('[data-widget-id]');
    
    let maxBottom = 0;
    sectionChildren.forEach(child => {
      const bottom = child.getBoundingClientRect().bottom;
      maxBottom = Math.max(maxBottom, Math.ceil(bottom - innerTop));
    });
    return maxBottom;
  };
  
  useEffect(() => {
    const sectionColumn = ref.current;
    if (!sectionColumn) return;

    if (flexMode) {
      return;
    }

    if (!mobile) {
      sectionColumn.style.minHeight = '';
      return;
    }

    const minHeight = getColumnBottomBoundary(sectionColumn);
    const currentColumnHeight = sectionColumn.getBoundingClientRect().height;
    const currentHeight = _.get(widgetProps, `properties.${breakpoint}.layout.height`);
    
    const prevBottom = prevBottomRef.current;
    const isResized = !prevBottom || minHeight > prevBottom;
    prevBottomRef.current = minHeight;
    const isHeightChanged = isResized && minHeight > 0 && minHeight > currentColumnHeight;
    
    sectionColumn.style.minHeight = minHeight > 0 ? `${minHeight}px` : '';
    
    if (isHeightChanged) {
      sectionColumn.style.height = `${minHeight}px`;
      
      currentHeight !== minHeight && dispatch(
        updateWidgetProperty(editor, id, 'mobile', {
          layout: {
            ...mobilelayout,
            height: `${minHeight}px`
          }
        })
      );
    }
  }, [children, flexMode]);

  return (
    <>
      <Container widgetProps={widgetProps} widgetRef={widgetRef}>
        <div
          id={id}
          ref={ref}
          className={cx(styles.column, 'widget', {
            [styles.selected]: selected,
            [styles.parentSelected]: parentSelected,
            [styles.partialSelection]: childrenSelection.selected,
            [styles.flexMode]: flexMode
          })}
          style={widgetStyle}
          data-widget-id={id}
          data-container={true}
          data-widget-type={type}
          data-selectable={parentSelected || siblingSelection.selected}
        >
          <BackgroundView background={background} partialSelection={childrenSelection.selected}/>
          <FlexFlowContext.Provider value={flexContextValue}>
            <div
              className={flexMode ? styles.flexInner : styles.inner}
              style={flexMode ? flexInnerStyle : undefined}
              data-flex-inner={flexMode ? 'true' : undefined}
            >
              {children}
            </div>
          </FlexFlowContext.Provider>
          <VisibleArea id={id} container={container} editorContext={editorContext} stretched={stretched} breakpoint={breakpoint} childrenSelection={childrenSelection}/>
        </div>
      </Container>
    </>
  );
};

const VisibleArea = ({ id, container, editorContext, stretched, childrenSelection, breakpoint }) => {
  const [state, setState] = useState(null);


  const visibleWidth = _.get(container, `minWidth`);
  const alignment = _.get(container, `alignment`) || 'center';
  const showGridCondition = childrenSelection?.selected && stretched

  var multiplier = alignmentMultiplier(alignment);

  var areaStyles = {
    width: `${visibleWidth}px`,
    marginLeft: `calc((100% - ${visibleWidth}px) * ${multiplier})`
  }

  useEffect(() => {
    const handleParentHover = (data) => {
      setState(data);
    };
    const handleParentChange = () => {
      setState(null);
    };
    editorContext.editorSession.eventBus.on("onParentHover", handleParentHover);
    editorContext.editorSession.eventBus.on("onParentChange", handleParentChange);
    return () => {
      editorContext.editorSession.eventBus.off("onParentHover", handleParentHover);
      editorContext.editorSession.eventBus.off("onParentChange", handleParentChange);
    };
  }, []);

  return (
    <>
      <div className={cx(styles.boundary, styles.left)} style={{
        left: `calc((100% - ${visibleWidth}px) * ${multiplier})`,
      }} />
      {alignment!=='left' &&
        <div
          id={showGridCondition && breakpoint === 'desktop' && `gridOutLeft`}
          className={cx(styles.gridOutCon, {[styles.gridOut]: _.get(state, 'gridOut')})}
          style={{width: `calc((100% - ${visibleWidth}px) * ${multiplier})`, left: 0}}>
        </div>
      }
      <div className={cx(styles.boundary, styles.right)} style={{
        right: `calc((100% - ${visibleWidth}px) * ${1 - multiplier})`,
      }} />
      {alignment!=='right' && 
        <div
          id={showGridCondition && breakpoint === 'desktop' && `gridOutRight`}
          className={cx(styles.gridOutCon, {[styles.gridOut]: _.get(state, 'gridOut')})}
          style={{width: `calc((100% - ${visibleWidth}px) * ${1 - multiplier})`, right:0}}>
        </div>
      }  
      <div id={`${id}_visible_area`} className={cx(styles.visibleArea, 'cs_visible_area__content')} style={areaStyles}>
      </div>
    </>
  );
}

const SectionColumnWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardContext = React.useContext(StoryboardContext);

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');
  const selectedParent = useContainerSelector(editorSession, id);
  const stretched = _.get(selectedParent, 'properties.desktop.stretched', false);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const handleManageColumns = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);

    dispatch(updateWidgetEditorState(editorSession, props.parent, {
      manageColumns: true
    }));
  }

  const closePopup = () => {
    setView(null);
    dispatch(toggleEditMode(editorSession, props.uid, false));
    dispatch(updateWidgetEditorState(editorSession, props.parent, {
      manageColumns: false
    }));
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.textButton} onClick={handleManageColumns}>
        Manage Columns
      </Property.Action>

      {!PAGE_CONTAIN_STORYBOARDS.includes(storyboardContext.type) && stretched &&
        <>
          <Property.Divider />
          <Property.Action className={styles.iconButton} tooltip='Layout' onClick={() => changeView('alignment')}>
            <i className={cx("cleverstory-layout")} />
          </Property.Action>
        </>
      }

      {view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          widgetProps={props}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {view === 'alignment' &&
        <ColumnLayoutEditor
          id={id}
          parent={props.parent}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const SectionColumnWidgetMobileAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardType = _.get(React.useContext(StoryboardContext), 'type');
  const entityType = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`));

  const id = _.get(props, 'uid');
  const flexMode = _.get(props, 'properties.desktop.flexBoxEnabled', false);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    setView(null);
    dispatch(toggleEditMode(editorSession, props.uid, false));
  }

  if (!isFlexModeAllowed(storyboardType, entityType) || !flexMode) {
    return false;
  }

  return (
    <>
      <Property.Action className={styles.textButton} tooltip='Flex Layout (Mobile)' onClick={() => changeView('flexConfig')}>
        Layout
      </Property.Action>

      {view === 'flexConfig' &&
        <FlexConfigDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const SectionColumnWidget = React.forwardRef((props, ref) => {
  return <SectionColumnWidgetView {...props} />
});

const ColumnLayoutEditor = ({ id, parent, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.desktop`, {});

  const container = _.get(properties, `container`);
  const alignment = _.get(properties, 'container.alignment', 'center');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, 'desktop', value));
  }

  const handleAlignmentChange = (value) => {
    handlePropertyChange({
      container: {
        ...container,
        alignment: value
      }
    });
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Manage Column'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Align content to:" className={styles.columnAlignmentProperty}>
          <div className={styles.columnAlignment}>
            <div className={styles.options}>
              <Tooltip placement="top" content="Left Align">
                <button className={cx(styles.alignment,
                  {
                    [styles.active]: alignment === 'left'
                  })}
                  onClick={handleAlignmentChange.bind(this, 'left')}
                >
                  <i className={cx('cleverstory-align-left')}></i>
                </button>
              </Tooltip>
              <Tooltip placement="top" content="Center Align">
                <button className={cx(styles.alignment,
                  {
                    [styles.active]: alignment === 'center'
                  })}
                  onClick={handleAlignmentChange.bind(this, 'center')}
                >
                  <i className={cx('cleverstory-align-center')}></i>
                </button>
              </Tooltip>
              <Tooltip placement="top" content="Right Align">
                <button className={cx(styles.alignment,
                  {
                    [styles.active]: alignment === 'right'
                  })}
                  onClick={handleAlignmentChange.bind(this, 'right')}
                >
                  <i className={cx('cleverstory-align-right')}></i>
                </button>
              </Tooltip>
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

function getStyles(widget, breakpoint, columnGap, flexMode, sectionHasContent, hasContent) {
  var styles = {};

  if (breakpoint === 'desktop') {
    const width = parseFloat(_.get(widget, `properties.${breakpoint}.layout.width`, 100));

    if (flexMode) {
      styles = {
        ...styles,
        maxWidth: `calc(${width}% - ${columnGap}px)`,
        height: 'auto',
        ...(sectionHasContent ? {} : { minHeight: '200px' }),
      }
    } else {
      styles = {
        ...styles,
        maxWidth: `calc(${width}% - ${columnGap}px)`,
        height: 'auto',
        minHeight: '100%',
      }
    }
  }

  if (breakpoint === 'mobile' && flexMode) {
    styles = {
      ...styles,
      height: 'auto',
      ...(hasContent ? {} : { minHeight: '200px' }),
    }
  }

  return styles;
}

SectionColumnWidget.actions = (editorSession, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return <SectionColumnWidgetDesktopAction {...props} />
  }

  if (breakpoint === 'mobile') {
    return <SectionColumnWidgetMobileAction {...props} />
  }

  return false;
}

SectionColumnWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background
      }
    }
  }
}

SectionColumnWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

SectionColumnWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const desktop = breakpoint === 'desktop';
  const flexMode = _.get(widget, 'properties.desktop.flexBoxEnabled', false);
  const resizeAllowed = !desktop && !flexMode;

  return {
    draggable: false,
    resizable: resizeAllowed,
    rotatable: false,
    renderDirections: resizeAllowed ? ['n', 's'] : [],
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('transform', 'translate', '0px, 0px');

      if(!layout.height) {
        frame.remove('height')
      }

      return frame.get();
    }
  }
}


export { SectionColumnWidget };